<template>
  <LoadingSpinner :show="UIStore.is_loading" size="large" />
  <div v-if="isReady && changeInvestmentStore.change_investment_steps" class="flex flex-col h-full">
    <CI_RestartChangeInvestmentModal
      :open="showProgressModal"
      @close-modal="showProgressModal = false"
      @discard="handleDiscard"
      @resume="handleResume"
    />
    <div class="pb-6">
      <ChangeInvestmentControlCenter
        @continue="handleContinue"
        @back="handleBack"
        @cancel-change="handleCancelChange"
      />
      <component
        ref="currentStepRef"
        class="w-full mt-[1.375rem] lg:mt-10"
        :is="
          getStepComponent(
            changeInvestmentStore.change_investment_steps[changeInvestmentStore.current_step - 1]
              ?.name
          )
        "
        :key="changeInvestmentStore.current_step"
        @substep-change="handleSubstepChange"
        @continue="handleContinue"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, watch, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePurchaseFlowStore, useUIStore, useDataStore, useChangeInvestmentStore } from "@/stores";
import { LoadingSpinner } from "@/components";

import CI_SetUpConversion from "./CI_SetUpConversion.vue";
import PF_SuitabilityInfo from "../PurchaseFlow/PF_SuitabilityInfo.vue";
import CI_FinalizeConversion from "./CI_FinalizeConversion.vue";
import ChangeInvestmentControlCenter from "./ChangeInvestmentControlCenter.vue";
import CI_RestartChangeInvestmentModal from "./CI_RestartChangeInvestmentModal.vue";

const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();
const dataStore = useDataStore();
const changeInvestmentStore = useChangeInvestmentStore();

const route = useRoute();
const router = useRouter();

const currentStepRef = ref(null);
const showProgressModal = ref(false);
const isReady = ref(false);

const stepComponents = {
  "Change Investment": CI_SetUpConversion,
  "Suitability Questionnaire": PF_SuitabilityInfo,
  "Sign and Finalize": CI_FinalizeConversion,
};

onMounted(async () => {
  window.scrollTo(0, 0);
  UIStore.is_loading = true;
  if (!route.query.recordId) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message:
        "No investment selected for change. Please select an investment to change from the Investment Details page.",
    });
    router.push("/portfolio");
    return;
  }

  if (route.query.resumeFromRedirect) {
    await changeInvestmentStore.resumeFromRedirect(route.query.recordId);
    router.replace({ query: { recordId: route.query.recordId } });
    isReady.value = true;
    UIStore.is_loading = false;
    return;
  }

  await changeInvestmentStore.initializeChangeInvestmentFlow(route.query.recordId);

  if (changeInvestmentStore.change_investment_children.some((c) => c.record_id)) {
    await changeInvestmentStore.fetchConversionDocuments();
  }

  isReady.value = true;
  UIStore.is_loading = false;
});

watch(
  () => changeInvestmentStore.current_step,
  (newStep) => {
    if (newStep) {
      window.scrollTo(0, 0);
    }
  }
);

const getStepComponent = (stepName) => {
  return stepComponents[stepName] || null;
};

async function handleDiscard() {
  showProgressModal.value = false;
  UIStore.is_loading = true;

  try {
    // Save current edited children before clearing
    const editedChildren = [...changeInvestmentStore.change_investment_children];

    await changeInvestmentStore.clearConversionProgress();

    // Restore edited children with cleared record_ids
    changeInvestmentStore.change_investment_children = editedChildren.map((c) => ({
      ...c,
      record_id: null,
    }));

    changeInvestmentStore.snapshotChildren();
    proceedToNextStep();
  } finally {
    UIStore.is_loading = false;
  }
}

function handleResume() {
  showProgressModal.value = false;
  // Revert children to what was saved/signed
  if (changeInvestmentStore.childrenSnapshot) {
    changeInvestmentStore.change_investment_children = changeInvestmentStore.childrenSnapshot.map(
      (snap) => {
        const offering = dataStore.offerings.find((o) => o.record_id === snap.offering_id);
        return {
          record_id: snap.record_id,
          amount: snap.amount,
          is_compounded: {
            value: snap.is_compounded,
            name: snap.is_compounded ? "At Maturity" : "Monthly",
          },
          offering: offering
            ? {
                name: !offering.is_short_term
                  ? `${offering.interest_rate}% (${offering.bond_term / 12} year term)`
                  : `${offering.interest_rate}% (${offering.bond_term} month term)`,
                value: offering,
              }
            : null,
        };
      }
    );
  }
  changeInvestmentStore.current_step = 3;
  changeInvestmentStore.current_substep = 1;
}

async function handleContinue() {
  const step = currentStepRef.value;
  const currentSubstep = purchaseFlowStore.current_substep;

  // 1. Run step-specific validation
  if (step?.validate) {
    const result = await step.validate();
    if (!result.proceed) {
      // dataStore.logInvestorActivity({ type: "validation_failed", validationResult: result });
      return;
    } else {
      // dataStore.logInvestorActivity({ type: "step_completed" });
    }
  }

  // 2. If on step 1, signing has started, and children changed — confirm restart
  if (
    changeInvestmentStore.current_step === 1 &&
    changeInvestmentStore.hasSigningProgress &&
    changeInvestmentStore.hasChildrenChanged
  ) {
    showProgressModal.value = true;
    return;
  }

  try {
    // 3. Proceed with navigation immediately
    proceedToNextStep();

    // 4. Fire off save in background
    if (step?.afterContinue) {
      step.afterContinue(currentSubstep).catch((error) => {
        console.error("Background save failed:", error);
      });
    }
  } catch (error) {
    console.error("Step error:", error);
  }
}

async function handleSubstepChange(newSubstep) {
  const step = currentStepRef.value;
  const currentSubstep = changeInvestmentStore.current_substep;

  // Same tab - do nothing
  if (newSubstep === currentSubstep) return;

  // Going backward - always allowed
  if (newSubstep < currentSubstep) {
    changeInvestmentStore.current_substep = newSubstep;
    return;
  }

  // Going forward - validate all substeps in between
  if (step?.validateRange) {
    const result = await step.validateRange(currentSubstep - 1, newSubstep - 1);

    if (!result.proceed) {
      // console.log(`Validation failed at "${result.tabName}":`, result.errors);

      changeInvestmentStore.current_substep = result.failedAt + 1;

      UIStore.animateNotificationAlert({
        type: "error",
        icon: "Info",
        message: `Please complete "${result.tabName}" first.`,
      });
      return;
    }
  }

  changeInvestmentStore.current_substep = newSubstep;

  // All validations passed - save
  if (step?.afterContinue) {
    step.afterContinue(currentSubstep).catch((error) => {
      console.error("Background save failed:", error);
    });
  }
}

async function proceedToNextStep() {
  const hasSubsteps = changeInvestmentStore.currentStep?.sub_steps?.length > 0;
  const totalSubsteps = changeInvestmentStore.currentStep?.sub_steps?.length || 0;

  if (hasSubsteps && changeInvestmentStore.current_substep < totalSubsteps) {
    changeInvestmentStore.current_substep += 1;
  } else if (
    changeInvestmentStore.current_step < changeInvestmentStore.change_investment_steps?.length
  ) {
    changeInvestmentStore.current_step += 1;
    changeInvestmentStore.current_substep = 1;
  } else {
    await handleCompleteInvestment();
  }
}

function handleBack() {
  const hasSubsteps = changeInvestmentStore.currentStep?.sub_steps?.length > 0;

  if (hasSubsteps && changeInvestmentStore.current_substep > 1) {
    changeInvestmentStore.current_substep -= 1;
  } else if (changeInvestmentStore.current_step >= 2) {
    changeInvestmentStore.current_step -= 1;
    const prevStepData =
      changeInvestmentStore.change_investment_steps[changeInvestmentStore.current_step - 1];
    changeInvestmentStore.current_substep = prevStepData?.sub_steps?.length || 1;
  } else {
    router.push(
      `/investment-details?recordId=${changeInvestmentStore.current_investment_for_change.record_id}`
    );
  }
}

async function handleCompleteInvestment() {
  // Still need to handle the finish later path since the polling will handle finalization from the backend now
  if (!changeInvestmentStore.allDocumentsSigned) {
    router.push(
      `/investment-details?recordId=${changeInvestmentStore.current_investment_for_change.record_id}`
    );
    return;
  }
}

async function handleCancelChange() {
  UIStore.is_loading = true;
  try {
    await changeInvestmentStore.clearConversionProgress();
    router.push(
      `/investment-details?recordId=${changeInvestmentStore.current_investment_for_change.record_id}`
    );
  } catch (err) {
    console.error("Failed to cancel change investment:", err);
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "ShieldAlert",
      message: "Failed to cancel. Please try again.",
      does_persist: true,
    });
  } finally {
    UIStore.is_loading = false;
  }
}
</script>
